/**
 * Sticky Tageskopf der Belegung (Board „Belegung“): Wochentag und Tag je Spalte, heute wald gefüllt,
 * Wochenende auf Papier, am Monatswechsel eine feine Linie mit der Monatsmarke („NOV“) – sie hält
 * die Orientierung, wenn ein Zeitraum über das Monatsende läuft.
 */
import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { isWeekend, monthBreakIndices, parseDate } from './calendarHelpers';

const dayShort = ['SO', 'MO', 'DI', 'MI', 'DO', 'FR', 'SA'];
const monthShort = [
  'JAN',
  'FEB',
  'MÄR',
  'APR',
  'MAI',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OKT',
  'NOV',
  'DEZ',
];

interface Props {
  dates: string[];
  sidebarWidth: number;
  cellMin: number;
  /** Was in der Ecke über der Beschriftung steht — Apartments oder Stellplätze (mit „Alle“). */
  sidebarLabel?: ReactNode;
  today?: string;
  /** Handy: niedriger und kleiner. */
  compact?: boolean;
}

export function CalendarHeader({
  dates,
  sidebarWidth,
  cellMin,
  sidebarLabel = 'Apartment',
  today,
  compact = false,
}: Props) {
  const breaks = monthBreakIndices(dates);
  return (
    <div
      className="bg-card border-hairline sticky top-0 z-20 grid border-b"
      style={{
        gridTemplateColumns: `${sidebarWidth}px repeat(${dates.length}, minmax(${cellMin}px, 1fr))`,
      }}
    >
      <div className="text-ink-soft bg-card sticky left-0 z-30 flex items-center px-[18px] font-mono text-[10px] uppercase tracking-[0.14em]">
        {sidebarLabel}
      </div>
      {dates.map((d, i) => {
        const day = parseDate(d);
        const isToday = Boolean(today && d === today);
        const isMonthBreak = breaks.has(i);
        return (
          <div
            key={d}
            aria-current={isToday ? 'date' : undefined}
            className={cn(
              // Unten ausgerichtet, damit oben Platz für die Monatsmarke bleibt.
              'border-card-alt relative flex flex-col items-center justify-end gap-0.5 border-l pb-1.5',
              compact ? 'h-12' : 'h-14',
              isWeekend(d) && !isToday && 'bg-paper',
              isToday && 'bg-forest text-white',
              isMonthBreak && 'border-l-ink-faint/40',
            )}
          >
            {isMonthBreak && (
              <span
                className={cn(
                  'absolute left-1 top-0.5 font-mono font-semibold leading-none tracking-[0.14em]',
                  compact ? 'text-[8px]' : 'text-[9px]',
                  isToday ? 'text-mint' : 'text-ink-soft',
                )}
              >
                {monthShort[day.getUTCMonth()]}
              </span>
            )}
            <span
              className={cn('font-bold tracking-[0.08em]', compact ? 'text-[9px]' : 'text-[10px]')}
            >
              {dayShort[day.getUTCDay()]}
            </span>
            <span
              className={cn(
                'font-mono font-semibold tabular-nums',
                compact ? 'text-[13px]' : 'text-sm',
              )}
            >
              {day.getUTCDate()}
            </span>
          </div>
        );
      })}
    </div>
  );
}
